// Dialog zum Anlegen/Bearbeiten einer Zahlungstext-Vorlage (Rich-Text-Umstellung, Teil 3)
//
// Vereinfachte Übertragung von CancellationTermFormDialog.tsx: KEINE Partnerzuordnung
// (kein `owner`-Feld, kein `canReassign`/`reassignmentFor`) — es gibt keine
// Depot-Vorlagen, nur Zentrale/Admin dürfen diese Seite überhaupt öffnen
// (Nutzerentscheidung, Plan Teil 3). Ansonsten wortgleicher Aufbau.

import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { RichTextEditor } from '@/features/events/RichTextEditor';
import { isBlankMarkdown } from '@/features/events/event-text-pick';
import type { PaymentMethod, PaymentNoteTemplate, SavePaymentNoteTemplateRequest } from '@/types';

interface Props {
  open: boolean;
  /** null = anlegen; sonst bearbeiten. */
  template: PaymentNoteTemplate | null;
  /** Serverfehler (409 der Sperre, 404): erscheint im Dialog, nicht als Toast. */
  saveError?: string | null;
  onOpenChange: (open: boolean) => void;
  onSave: (req: SavePaymentNoteTemplateRequest) => void;
  isSaving?: boolean;
}

export interface PaymentNoteTemplateFormValues {
  title: string;
  text: string;
  /** Zahlungsart-Zuordnung (15.09.2026) — `undefined` = keine Zuordnung. */
  paymentMethod?: PaymentMethod;
}

const emptyForm = (): PaymentNoteTemplateFormValues => ({ title: '', text: '' });

/** Die drei Werte, wie im VA-Formular (EventFormDialog.tsx). */
const PAYMENT_METHOD_OPTIONS: readonly PaymentMethod[] = ['depot', 'depot_or_transfer', 'transfer'];

/** Die Fehler, die diese Prüfung melden kann — als i18n-Schlüssel. */
export type PaymentNoteTemplateFormError =
  | 'paymentNoteTemplates.form.errors.title'
  | 'paymentNoteTemplates.form.errors.text';

/** Prüft das Formular und gibt einen i18n-Schlüssel zurück, oder null. */
export function validatePaymentNoteTemplateForm(
  values: PaymentNoteTemplateFormValues
): PaymentNoteTemplateFormError | null {
  if (values.title.trim().length === 0) return 'paymentNoteTemplates.form.errors.title';
  if (isBlankMarkdown(values.text)) return 'paymentNoteTemplates.form.errors.text';

  return null;
}

export function PaymentNoteTemplateFormDialog({
  open,
  template,
  saveError,
  onOpenChange,
  onSave,
  isSaving,
}: Props) {
  const { t } = useTranslation(['settings', 'events']);
  const [form, setForm] = useState<PaymentNoteTemplateFormValues>(emptyForm);
  const [error, setError] = useState<PaymentNoteTemplateFormError | null>(null);

  const isEdit = template !== null;

  useEffect(() => {
    if (!open) return;
    setForm(
      template
        ? { title: template.title, text: template.text, paymentMethod: template.paymentMethod }
        : emptyForm()
    );
    setError(null);
  }, [open, template]);

  const handleSubmit = () => {
    const problem = validatePaymentNoteTemplateForm(form);
    if (problem) {
      setError(problem);
      return;
    }

    setError(null);
    onSave({
      id: template?.id,
      title: form.title.trim(),
      text: form.text,
      paymentMethod: form.paymentMethod,
      etag: template?.etag,
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <DialogTitle>
            {t(isEdit ? 'paymentNoteTemplates.form.titleEdit' : 'paymentNoteTemplates.form.titleCreate')}
          </DialogTitle>
          <DialogDescription>{t('paymentNoteTemplates.form.description')}</DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-1.5">
            <label className="text-xs text-muted-foreground" htmlFor="payment-note-template-title">
              {t('paymentNoteTemplates.form.fields.title')}
            </label>
            <Input
              id="payment-note-template-title"
              value={form.title}
              onChange={(e) => setForm((prev) => ({ ...prev, title: e.target.value }))}
              placeholder="Rechnung"
            />
          </div>

          <div className="space-y-1.5">
            <label className="text-xs text-muted-foreground">
              {t('paymentNoteTemplates.form.fields.paymentMethod')}
            </label>
            <Select
              value={form.paymentMethod ?? 'null'}
              onValueChange={(v) =>
                setForm((prev) => ({ ...prev, paymentMethod: v === 'null' ? undefined : (v as PaymentMethod) }))
              }
            >
              <SelectTrigger>
                <SelectValue placeholder={t('paymentNoteTemplates.form.fields.paymentMethodPlaceholder')} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="null">{t('paymentNoteTemplates.form.fields.paymentMethodNone')}</SelectItem>
                {PAYMENT_METHOD_OPTIONS.map((v) => (
                  <SelectItem key={v} value={v}>{t(`events:paymentMethod.${v}`)}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-1.5">
            <label className="text-xs text-muted-foreground">
              {t('paymentNoteTemplates.form.fields.text')}
            </label>
            <RichTextEditor
              value={form.text}
              onChange={(markdown) => setForm((prev) => ({ ...prev, text: markdown }))}
            />
          </div>

          {error && <p className="text-sm text-red-600">{t(error)}</p>}
          {!error && saveError && <p className="text-sm text-red-600">{saveError}</p>}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            {t('paymentNoteTemplates.form.cancel')}
          </Button>
          <Button onClick={handleSubmit} disabled={isSaving}>
            {isSaving ? t('paymentNoteTemplates.form.saving') : t('paymentNoteTemplates.form.save')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
